import { useState } from 'react';
import { useRandomGitaVerse } from '../../lib/gita';

const normalizeText = (text: string) =>
  text.split('\n').map((line) => line.trimEnd()).join('\n').trim();

const normalizeShloka = (text: string) =>
  normalizeText(text)
    .replace(/।।\d+\.\d+।।/g, '।।')  // verse number shown in heading; keep closing danda
    .replace(/\n{2,}/g, '\n')
    .trim();

export { normalizeText, normalizeShloka };

export function BhagavadGita() {
  const { data: verse, isLoading, isError, refetch } = useRandomGitaVerse();

  const [activeTranslationId, setActiveTranslationId] = useState<number | null>(null);
  const [activeCommentaryId, setActiveCommentaryId] = useState<number | null>(null);
  const [showCommentary, setShowCommentary] = useState(false);

  if (isLoading) {
    return (
      <section className="bg-[var(--color-bg-alt)] py-20 px-6 sm:px-12 flex flex-col items-center justify-center min-h-[400px] text-center">
        <span className="text-5xl font-serif text-[var(--color-brand-muted)] animate-pulse">ॐ</span>
        <p className="mt-6 uppercase tracking-[0.2em] text-xs text-[var(--color-ink-mute)]">Loading verse…</p>
      </section>
    );
  }

  if (isError || !verse) {
    return null; // Fail silently so the landing page isn't disrupted
  }

  // Auto-select the first available author if not set or not in current list
  const currentTranslationId =
    activeTranslationId && verse.translations.some((t) => t.author_id === activeTranslationId)
      ? activeTranslationId
      : verse.translations[0]?.author_id;

  const currentCommentaryId =
    activeCommentaryId && verse.commentaries.some((c) => c.author_id === activeCommentaryId)
      ? activeCommentaryId
      : verse.commentaries[0]?.author_id;

  const currentTranslation = verse.translations.find((t) => t.author_id === currentTranslationId);
  const currentCommentary = verse.commentaries.find((c) => c.author_id === currentCommentaryId);

  return (
    <section className="bg-[var(--color-bg-alt)] py-24 px-6 sm:px-12 w-full">
      <div className="max-w-3xl mx-auto flex flex-col items-center text-center">
        <h2 className="mb-2 uppercase tracking-[0.2em] text-sm text-[var(--color-brand)] font-grotesk">
          Bhagavad Gita {verse.chapter}.{verse.verse}
        </h2>
        
        {/* Devanagari */}
        <p className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-snug md:leading-[1.4] text-[var(--color-ink)] mb-8 whitespace-pre-wrap">
          {normalizeShloka(verse.text)}
        </p>

        {/* Translation Section */}
        {currentTranslation && (
          <div className="w-full">
            <p className="font-display text-xl md:text-2xl leading-relaxed text-[var(--color-ink)] mb-6 text-left border-l-4 border-[var(--color-brand-muted)] pl-6 italic">
              {normalizeText(currentTranslation.description)}
            </p>
            
            {verse.translations.length > 1 && (
              <div className="flex items-center justify-start gap-4 mb-2 text-sm font-grotesk text-[var(--color-ink-mute)]">
                <span>Translation by:</span>
                <select 
                  className="bg-transparent border border-[var(--color-border)] rounded px-2 py-1 text-xs uppercase cursor-pointer hover:border-[var(--color-ink-mute)] outline-none"
                  value={currentTranslationId || ''}
                  onChange={(e) => setActiveTranslationId(Number(e.target.value))}
                >
                  {verse.translations.map((t) => (
                    <option key={t.author_id} value={t.author_id}>{t.author_name}</option>
                  ))}
                </select>
              </div>
            )}
            {verse.translations.length === 1 && (
               <p className="text-left text-xs uppercase tracking-[0.1em] text-[var(--color-ink-mute)] font-grotesk">
                 — Translation by {currentTranslation.author_name}
               </p>
            )}
          </div>
        )}

        {/* Commentary Section */}
        {verse.commentaries.length > 0 && (
          <div className="w-full mt-10 flex flex-col items-center">
            {!showCommentary ? (
              <button 
                onClick={() => setShowCommentary(true)}
                className="btn-app-ghost is-small"
              >
                Expand to Read Commentary
              </button>
            ) : (
             <div className="w-full bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] p-6 md:p-10 shadow-sm transition-all duration-500 ease-in-out text-left mt-4 relative">
                <button 
                  onClick={() => setShowCommentary(false)} 
                  className="absolute top-6 right-6 text-[var(--color-ink-mute)] hover:text-[var(--color-ink)] transition-colors text-2xl leading-none font-light"
                  aria-label="Close commentary"
                >
                  &times;
                </button>
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 pb-4 border-b border-[var(--color-border)] gap-4">
                  <h3 className="font-grotesk uppercase tracking-[0.1em] text-sm font-semibold text-[var(--color-ink)]">
                    Commentary
                  </h3>
                  
                  {verse.commentaries.length > 1 ? (
                    <div className="flex items-center gap-2 text-xs font-grotesk">
                      <span className="text-[var(--color-ink-mute)]">Author:</span>
                      <select 
                        className="bg-transparent border border-[var(--color-border)] rounded px-2 py-1 text-[var(--color-ink)] uppercase cursor-pointer hover:border-[var(--color-ink-mute)] outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
                        value={currentCommentaryId || ''}
                        onChange={(e) => setActiveCommentaryId(Number(e.target.value))}
                      >
                        {verse.commentaries.map((c) => (
                          <option key={c.author_id} value={c.author_id}>{c.author_name}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <span className="text-xs uppercase tracking-[0.1em] text-[var(--color-ink-mute)] font-grotesk">
                      {currentCommentary?.author_name}
                    </span>
                  )}
                </div>
                
                <div className="font-serif text-lg leading-relaxed text-[var(--color-ink)] space-y-4 whitespace-pre-wrap">
                  {currentCommentary ? normalizeText(currentCommentary.description) : null}
                </div>
             </div>
            )}
          </div>
        )}

        <button 
          onClick={() => {
             setShowCommentary(false);
             refetch();
          }}
          className="mt-16 text-xs uppercase tracking-[0.15em] font-grotesk text-[var(--color-brand)] hover:text-[var(--color-ink)] transition-colors flex items-center gap-2"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.92-10.44l5.46-3.04"/></svg>
          Read Another Verse
        </button>
      </div>
    </section>
  );
}
